/**
 * DataIntelligencePanel — React UI for Data Intelligence.
 *
 * Renders ALL Data Intelligence controls:
 * - DataIntelligenceBar (top bar)
 * - BucketPanel (bottom bar)
 * - Data source selector (Photos / O'Neal)
 * - Texture Atlas info + face mode buttons
 * - Debug BBox toggle
 * - Nudge buttons
 * - Atlas selector (load by ID)
 * - Clear atlas button
 * - Depth field buttons
 * - Sort button
 * - Category filter buttons
 *
 * NO business logic — delegates everything to DataIntelligenceController.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import * as THREE from 'three';
import DataIntelligenceBar from './DataIntelligenceBar';
import BucketPanel from './BucketPanel';
import { DataIntelligenceController, DEPTH_FIELDS, type DataSourceKind } from '@arkturian/wave-engine/data/DataIntelligenceController';

interface DataIntelligencePanelProps {
    controller: DataIntelligenceController;
    store: any;
    meshRef: React.RefObject<THREE.InstancedMesh>;
    uniformsRef: React.MutableRefObject<any>;
    overlayOnly?: boolean;  // render only the fixed top bar (DataIntelligenceBar)
    bucketsOnly?: boolean;  // render only the bucket chips, inline (for the page's bottom dock)
    panelOnly?: boolean;    // render only panel content (buttons inside the side panel)
}

const DataIntelligencePanel: React.FC<DataIntelligencePanelProps> = ({ controller, store, meshRef, uniformsRef, overlayOnly, bucketsOnly }) => {
    // Force re-render counter — incremented on controller state changes
    const [, setTick] = useState(0);
    const forceUpdate = useCallback(() => setTick(t => t + 1), []);

    // Subscribe to controller state changes
    useEffect(() => {
        return controller.subscribe(forceUpdate);
    }, [controller, forceUpdate]);

    // Subscribe to store values needed for rendering
    const activeAtlasId = store((state: any) => state.activeAtlasId);
    const atlasRows = store((state: any) => state.atlasRows);
    const atlasCols = store((state: any) => state.atlasCols);
    const atlasLodCount = store((state: any) => state.atlasLodCount);

    // Initialize filter texture in uniforms once available
    useEffect(() => {
        controller.initFilterTexture();
    }, [uniformsRef.current]);

    // Auto-load tile data when atlas is active (wait for mesh to be ready)
    const atlasIdForAutoLoad = store((s: any) => s.activeAtlasId);
    const autoLoadTriggered = useRef(false);
    useEffect(() => {
        if (!atlasIdForAutoLoad || autoLoadTriggered.current) return;
        let cancelled = false;
        const tryLoad = () => {
            if (cancelled) return;
            if (meshRef.current) {
                autoLoadTriggered.current = true;
                controller.autoLoadTileData(atlasIdForAutoLoad);
            } else {
                setTimeout(tryLoad, 500);
            }
        };
        setTimeout(tryLoad, 1000);
        return () => { cancelled = true; };
    }, [atlasIdForAutoLoad]);

    // --- Handlers (delegate to controller) ---

    const loadById = (id: string) => {
        if (!meshRef.current) return;
        setTimeout(() => {
            if (meshRef.current) store.getState().loadAtlasById(meshRef.current, id);
        }, 100);
    };

    const clearAtlas = () => {
        if (!meshRef.current) return;
        store.getState().clearAtlas(meshRef.current);
    };

    const handleSourceChange = (src: DataSourceKind) => {
        if (!meshRef.current) return;
        if (src === 'oneal') controller.loadOnealProducts();
        else if (src === 'agentos') controller.loadAgentOs();
        else if (src === 'photos') controller.loadPhotoData();
        else controller.loadSource(src);   // registered via registerDataSource()
    };

    const handleDepthFieldChange = (field: string) => {
        controller.setDepthField(field);
    };

    const handleSort = () => {
        controller.sortByActiveField();
    };

    const handleColumnSwitch = (columnName: string) => {
        controller.switchColumn(columnName);
    };

    const handleBucketSelect = (bucket: any | null) => {
        controller.selectBucket(bucket);
    };

    const handleNudge = (dx: number) => {
        controller.nudge(dx);
    };

    // --- Derived values ---

    const depthFields = DEPTH_FIELDS[controller.dataSource];

    const registry = store.getState().getAtlasRegistry();

    // Buckets-only: the chip row, inline inside the page's bottom dock (no fixed positioning — it used to
    // sit on top of the shape bar, the ◀ ▶ arrows and the gear; Alex' screenshot 124999, 2026-09-13)
    if (bucketsOnly) {
        return (
            <BucketPanel
                inline
                buckets={controller.buckets}
                activeBucket={controller.activeBucketLabel}
                onSelect={handleBucketSelect}
                columnLabel={controller.activeColumn || undefined}
            />
        );
    }
    // Overlay-only mode: just the fixed top bar
    if (overlayOnly) {
        return (
            <>
                <DataIntelligenceBar
                    dataSource={controller.dataSource}
                    onSourceChange={handleSourceChange}
                    loading={controller.onealLoading}
                    onealCount={controller.onealCount}
                    depthFields={depthFields}
                    activeDepthField={controller.activeField}
                    onDepthFieldChange={handleDepthFieldChange}
                    onSort={handleSort}
                    categoricColumns={controller.categoricColumns}
                    activeColumn={controller.activeColumn || ''}
                    onColumnChange={handleColumnSwitch}
                    onAutoRecommend={() => { controller.useRecommendedDimension(); forceUpdate(); }}
                    recommendedColumn={controller.getRecommendedDimension()?.columnName}
                    agentCount={controller.agentCount}
                    agentFace={controller.agentFace}
                    agentStatus={controller.agentError ? `Fehler: ${controller.agentError}` : controller.agentProgress}
                    agentLoading={controller.agentLoading}
                    onAgentFaceChange={(f) => controller.setAgentFace(f)}
                />
            </>
        );
    }

    // Panel-only or default mode: render panel content
    return (
        <div style={{ marginBottom: '1.5rem' }}>
            {/* Data Intelligence — always visible */}
            <h2 style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '1rem', marginBottom: '0.75rem' }}>Data Intelligence</h2>
            <div style={{ display: 'flex', gap: '2px', marginBottom: '0.75rem', border: '1px solid #334155', borderRadius: '0.375rem', padding: '2px' }}>
                <button
                    onClick={() => handleSourceChange('photos')}
                    style={{
                        flex: 1, padding: '0.5rem 0.25rem', fontSize: '0.75rem', fontWeight: 600,
                        color: controller.dataSource === 'photos' ? '#f1f5f9' : '#34d399',
                        backgroundColor: controller.dataSource === 'photos' ? '#059669' : 'rgba(52, 211, 153, 0.1)',
                        border: 'none', borderRadius: '0.25rem', cursor: 'pointer',
                    }}
                >
                    Photos
                </button>
                <button
                    onClick={() => handleSourceChange('oneal')}
                    disabled={controller.onealLoading}
                    style={{
                        flex: 1, padding: '0.5rem 0.25rem', fontSize: '0.75rem', fontWeight: 600,
                        color: controller.dataSource === 'oneal' ? '#f1f5f9' : '#f97316',
                        backgroundColor: controller.dataSource === 'oneal' ? '#ea580c' : 'rgba(249, 115, 22, 0.1)',
                        border: 'none', borderRadius: '0.25rem', cursor: 'pointer',
                        opacity: controller.onealLoading ? 0.5 : 1,
                    }}
                >
                    {controller.onealLoading ? 'Loading...' : `O'Neal${controller.onealCount ? ` (${controller.onealCount})` : ''}`}
                </button>
                <button
                    onClick={() => handleSourceChange('agentos')}
                    disabled={controller.agentLoading}
                    style={{
                        flex: 1, padding: '0.5rem 0.25rem', fontSize: '0.75rem', fontWeight: 600,
                        color: controller.dataSource === 'agentos' ? '#f1f5f9' : '#a78bfa',
                        backgroundColor: controller.dataSource === 'agentos' ? '#7c3aed' : 'rgba(167, 139, 250, 0.1)',
                        border: 'none', borderRadius: '0.25rem', cursor: 'pointer',
                        opacity: controller.agentLoading ? 0.5 : 1,
                    }}
                >
                    {controller.agentLoading ? '…' : `AgentOS${controller.agentCount ? ` (${controller.agentCount})` : ''}`}
                </button>
            </div>

            <h2 style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '1rem', marginBottom: '0.75rem' }}>Texture Atlas</h2>
            {activeAtlasId ? (
                <div>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                        <strong>{activeAtlasId}</strong> — {atlasRows}x{atlasCols} = {atlasRows * atlasCols} tiles, {atlasLodCount} LOD{atlasLodCount !== 1 ? 's' : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '0.5rem', border: '1px solid #334155', borderRadius: '0.375rem', padding: '2px' }}>
                        {[
                            { label: 'Alle Faces', mode: 0 },
                            { label: 'Vorne+Hinten', mode: 1 },
                            { label: 'Nur Vorne', mode: 2 },
                        ].map(opt => (
                            <button
                                key={opt.mode}
                                onClick={() => store.getState().setAtlasFaceMode(opt.mode)}
                                style={{
                                    flex: 1, padding: '0.375rem 0.25rem', fontSize: '0.65rem',
                                    color: store.getState().atlasFaceMode === opt.mode ? '#f1f5f9' : '#94a3b8',
                                    backgroundColor: store.getState().atlasFaceMode === opt.mode ? '#4f46e5' : 'transparent',
                                    border: 'none', borderRadius: '0.25rem', cursor: 'pointer',
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => { const s = store.getState(); s.setShowDebugBBox(!s.showDebugBBox); }}
                        style={{
                            padding: '0.375rem 0.75rem', fontSize: '0.75rem',
                            color: store.getState().showDebugBBox ? '#fbbf24' : '#94a3b8',
                            backgroundColor: store.getState().showDebugBBox ? 'rgba(251, 191, 36, 0.15)' : 'transparent',
                            border: '1px solid rgba(251, 191, 36, 0.3)',
                            borderRadius: '0.25rem', cursor: 'pointer', width: '100%', marginBottom: '0.25rem',
                        }}
                    >
                        Debug BBoxes {store.getState().showDebugBBox ? 'ON' : 'OFF'}
                    </button>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '0.25rem' }}>
                        <button onClick={() => handleNudge(-1)} style={{ flex: 1, padding: '0.375rem', fontSize: '0.65rem', color: '#fb923c', backgroundColor: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.3)', borderRadius: '0.25rem', cursor: 'pointer' }}>
                            Nudge Left
                        </button>
                        <button onClick={() => handleNudge(1)} style={{ flex: 1, padding: '0.375rem', fontSize: '0.65rem', color: '#fb923c', backgroundColor: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.3)', borderRadius: '0.25rem', cursor: 'pointer' }}>
                            Nudge Right
                        </button>
                    </div>
                    {/* Depth Field Buttons — dynamic based on active dataset */}
                    <div style={{ color: '#64748b', fontSize: '0.6rem', marginBottom: '0.15rem', marginTop: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Depth Field</div>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                        {depthFields.map(opt => (
                            <button
                                key={opt.field}
                                onClick={() => handleDepthFieldChange(opt.field)}
                                style={{
                                    flex: 1, padding: '0.375rem 0.25rem', fontSize: '0.65rem', minWidth: '60px',
                                    color: controller.activeField === opt.field ? '#f1f5f9' : '#a78bfa',
                                    backgroundColor: controller.activeField === opt.field ? '#7c3aed' : 'rgba(167, 139, 250, 0.1)',
                                    border: 'none', borderRadius: '0.25rem', cursor: 'pointer',
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={handleSort}
                        style={{
                            padding: '0.375rem 0.75rem', fontSize: '0.75rem', color: '#38bdf8',
                            backgroundColor: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.3)',
                            borderRadius: '0.25rem', cursor: 'pointer', width: '100%', marginBottom: '0.25rem',
                        }}
                    >
                        Sort by {controller.activeField || 'field'}
                    </button>
                    {/* Category Filter (Bucket Panel) */}
                    {controller.categoricColumns.length > 0 && (
                        <>
                            <div style={{ color: '#64748b', fontSize: '0.6rem', marginBottom: '0.15rem', marginTop: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category Filter</div>
                            <div style={{ display: 'flex', gap: '2px', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                                {controller.categoricColumns.map((col) => (
                                    <button
                                        key={col}
                                        onClick={() => handleColumnSwitch(col)}
                                        style={{
                                            flex: 1, padding: '0.375rem 0.25rem', fontSize: '0.65rem', minWidth: '60px',
                                            color: controller.activeColumn === col ? '#f1f5f9' : '#fb923c',
                                            backgroundColor: controller.activeColumn === col ? '#ea580c' : 'rgba(251, 146, 60, 0.1)',
                                            border: 'none', borderRadius: '0.25rem', cursor: 'pointer',
                                        }}
                                    >
                                        {col}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    <button
                        onClick={clearAtlas}
                        style={{
                            padding: '0.375rem 0.75rem', fontSize: '0.75rem', color: '#f87171',
                            backgroundColor: 'rgba(248, 113, 113, 0.1)', border: '1px solid rgba(248, 113, 113, 0.3)',
                            borderRadius: '0.25rem', cursor: 'pointer', width: '100%',
                        }}
                    >
                        Atlas entfernen
                    </button>
                </div>
            ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {Object.values(registry).map((entry: any) => (
                        <button
                            key={entry.id}
                            onClick={() => loadById(entry.id)}
                            style={{
                                flex: 1, padding: '0.375rem 0.5rem', fontSize: '0.65rem', color: '#f1f5f9',
                                backgroundColor: entry.lods[0].urls[0].endsWith('.mp4') ? '#7c3aed' : '#4f46e5',
                                border: 'none', borderRadius: '0.25rem', cursor: 'pointer', minWidth: '80px',
                            }}
                        >
                            {entry.id} ({entry.tileCount}, {entry.lods.length} LOD)
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DataIntelligencePanel;
